/**
 * While read tracing is on, the banner over the audit list: who started it, for whom, why, and when it ends. Every
 * audit reader sees it, so nobody reads a traced log unaware; holders of `audit.tracing.manage` stop it here.
 *
 * The state is asked every minute (`useAuditTracing`); between asks the countdown runs locally from the answer's
 * `remaining_seconds`, its text changing once a minute. When it reaches zero the state is asked again: the banner
 * goes and a status line (read by screen readers) says "Read tracing ended", as it does when the poll finds tracing
 * gone before its end. Screens raise no toasts (WEB-DESIGN, Toast); a Stop's own toast comes from its mutation.
 */

import { useEffect, useRef, useState } from 'react';
import { Eye } from 'lucide-react';

import { useAuditTracing, useStopTracing } from '@/api/queries';
import type { AuditTracing } from '@/api/types';
import { useSession } from '@/session/SessionContext';

const MINUTE_MS = 60_000;
/** In the last minutes the countdown adds "ending soon". */
const ENDING_SOON_MS = 10 * MINUTE_MS;
/** After the countdown reaches zero, how long before asking again (the server's seconds are rounded down). */
const ENDED_GRACE_MS = 1000;

/** "Ends in 12 min.", "Ends in 8 min, ending soon." or "Ends in less than a minute, ending soon." */
export function countdownText(leftMs: number): string {
  const when = leftMs <= MINUTE_MS ? 'less than a minute' : `${Math.ceil(leftMs / MINUTE_MS)} min`;
  return leftMs <= ENDING_SOON_MS ? `Ends in ${when}, ending soon.` : `Ends in ${when}.`;
}

/** The reason as a sentence: a full stop unless it already ends in one. */
function sentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/** The milliseconds left, re-read at each whole minute left (and asking again once none are). */
function useCountdown(endsAt: number | null, onEnd: () => void): number {
  // Only a tick: each whole minute left renders again, and the render reads the clock.
  const [now, setNow] = useState(() => Date.now());
  const left = endsAt === null ? 0 : endsAt - Date.now();
  const end = useRef(onEnd);
  end.current = onEnd;

  useEffect(() => {
    if (endsAt === null) return undefined;
    const remaining = endsAt - Date.now();
    if (remaining <= 0) {
      const timer = setTimeout(() => end.current(), ENDED_GRACE_MS);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => setNow(Date.now()), remaining % MINUTE_MS || MINUTE_MS);
    return () => clearTimeout(timer);
  }, [endsAt, now]);

  return left;
}

/** Seen on, now off: unless this page stopped it (its own toast says so), tracing ended. */
function useEnded(data: AuditTracing | undefined) {
  const wasActive = useRef(false);
  const stoppedHere = useRef(false);
  const [ended, setEnded] = useState(false);
  useEffect(() => {
    if (!data) return;
    if (wasActive.current && !data.active && !stoppedHere.current) setEnded(true);
    if (data.active) {
      stoppedHere.current = false;
      setEnded(false);
    }
    wasActive.current = data.active;
  }, [data]);
  return { ended, stoppedHere };
}

function BannerText({ tracing, left }: { tracing: AuditTracing; left: number }) {
  return (
    <div className="a-body">
      <b>Read tracing is on</b>, started by {tracing.enabled_by ?? 'unknown'} for {tracing.user ?? 'everyone'}:{' '}
      <span className="wrap-anywhere">{sentence(tracing.reason ?? '')}</span>{' '}
      <span role="status">{countdownText(left)}</span>
    </div>
  );
}

export function TracingBanner() {
  const tracing = useAuditTracing();
  const stop = useStopTracing();
  const { can } = useSession();
  const data = tracing.data;
  const endsAt = data?.active ? tracing.dataUpdatedAt + (data.remaining_seconds ?? 0) * 1000 : null;
  const left = useCountdown(endsAt, () => void tracing.refetch());
  const { ended, stoppedHere } = useEnded(data);

  if (!data?.active) {
    return ended ? (
      <span className="sr-only" role="status">
        Read tracing ended
      </span>
    ) : null;
  }
  return (
    <div className="alert alert-warning form-alert">
      <Eye aria-hidden="true" />
      <BannerText tracing={data} left={left} />
      {can('audit.tracing.manage') && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={stop.isPending}
          onClick={() => {
            stoppedHere.current = true;
            stop.mutate(undefined, { onError: () => (stoppedHere.current = false) });
          }}
        >
          Stop
        </button>
      )}
    </div>
  );
}
